/**
 * How long the app remembers what it was in the middle of.
 *
 * Refreshing must not hand the app back its own opening guess: he is standing on a platform and
 * the app is not, so the direction he picked and the phase he stepped to have to survive a
 * reload. They must not survive the day, though — a morning left on screen is the wrong answer
 * by lunchtime, and the ride home is the wrong answer overnight.
 *
 * So the rule is idleness rather than a clock: choices last while the app is being used, and
 * lapse once it has been left alone for an hour. Then the normal derivation — the time of day
 * and where the phone is standing — gets to speak again.
 */

export const STICKY_IDLE_MS = 60 * 60_000;

/** Whether a session left this recently still owns its choices. */
export function isStickySession(lastSeenMs: number | null, nowMs: number): boolean {
	if (lastSeenMs == null) {
		return false;
	}
	const idleMs = nowMs - lastSeenMs;
	// A negative gap means the clock moved backwards, which is no evidence of a live session.
	return idleMs >= 0 && idleMs < STICKY_IDLE_MS;
}

export type Direction = "school" | "home";

export interface StoredDirection {
	direction: Direction;
	/** True when he said so himself, which stops the phone arguing with him.  */
	chosen: boolean;
}

function isDirection(value: unknown): value is Direction {
	return value === "school" || value === "home";
}

export function serialiseDirection(stored: StoredDirection): string {
	return JSON.stringify(stored);
}

export function parseDirection(raw: string | null): StoredDirection | null {
	if (!raw) {
		return null;
	}
	try {
		const parsed = JSON.parse(raw) as Partial<StoredDirection>;
		return isDirection(parsed.direction) ? { direction: parsed.direction, chosen: parsed.chosen === true } : null;
	} catch {
		return null;
	}
}

/** The opening guess, for a phone that has been left alone long enough to have no opinion. */
export function directionFromClock(nowMs: number): Direction {
	return new Date(nowMs).getHours() < 12 ? "school" : "home";
}
